import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import swal from 'sweetalert'
import { axiosInstance } from '../Config'
import { useNavigate, useParams } from 'react-router-dom'
import { usePermissionData } from '../../AppContext'

const VideosGallery = () => {
    const { kidKey } = useParams()
    const navigate = useNavigate()
    const [kid, setKid] = useState(null)
    const [videos, setVideos] = useState([])
    const [loading, setLoading] = useState(false)
    const [player, setPlayer] = useState(null)

    useEffect(() => {
        window.scrollTo(0, 0)
        fetchGallery()
    }, [])

    const fetchGallery = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get(`generated-videos/${kidKey}`)
            const data = res.data?.data ?? res.data
            setKid(data.kid)
            const vids = data.videos ?? []
            setVideos(vids)
            document.title = `${data.kid?.username ?? 'Kid'}'s Videos | Mindora`
        } catch {
            swal('', 'Failed to load videos.', 'error')
        } finally {
            setLoading(false)
        }
    }

    const onDelete = (videoId) => {
        swal({ title: "Delete Video?", text: "This video will be permanently removed.", icon: "warning", buttons: true, dangerMode: true })
            .then(async (will) => {
                if (!will) return
                setLoading(true)
                try {
                    await axiosInstance.delete(`generated-videos/${videoId}`)
                    swal("", "Video deleted successfully!", "success")
                    setVideos(prev => prev.filter(vid => vid.id !== videoId))
                    if (player?.id === videoId) setPlayer(null)
                } catch {
                    swal("", "Something went wrong!", "error")
                } finally {
                    setLoading(false)
                }
            })
    }

    return (
        <>
            {loading && <Loader />}
            <ContainerDashboard title={kid ? `${kid.username}'s Videos` : "Videos"} description="AI-generated videos by this kid">
                <section className="ps-dashboards">
                    <div className="p-4 py-5 bg-gradientt rounded-4" style={{ minHeight: '75vh' }}>
                        <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-4 flex-wrap"
                            style={{ background: 'linear-gradient(135deg, rgba(12,172,194,0.08), rgba(164,76,215,0.06))', border: '1px solid rgba(12,172,194,0.12)' }}>
                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 48, height: 48, background: 'linear-gradient(135deg, #0CACC2, #A44CD7)', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                                {kid?.username?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div className="flex-grow-1">
                                <p className="fw-bold mb-0" style={{ color: '#1a1a2e' }}>
                                    {kid?.username ?? '—'}
                                    <span style={{ fontWeight: 400, color: '#999', fontSize: 13 }}> · Age {kid?.age}</span>
                                </p>
                                {kid?.parent?.name && (
                                    <p className="mb-0" style={{ fontSize: 13, color: '#666' }}>Parent: {kid.parent.name}</p>
                                )}
                            </div>
                            <span className="badge rounded-pill px-3 py-2"
                                style={{ background: 'rgba(12,172,194,0.12)', color: '#0CACC2', fontWeight: 600, fontSize: 13 }}>
                                {videos.length} video{videos.length !== 1 ? 's' : ''}
                            </span>
                            <button type="button" className="button flex-shrink-0"
                                style={{ background: '#1a1a2e', color: '#fff', borderRadius: '12px' }}
                                onClick={() => navigate(-1)}>
                                <i className="icon-arrow-left me-2"></i>Back
                            </button>
                        </div>
                        {videos.length === 0 && !loading ? (
                            <div className="text-center py-5" style={{ color: '#aaa' }}>
                                <i className="bi bi-film" style={{ fontSize: 52, opacity: 0.3 }}></i>
                                <p className="mt-3 mb-0">No videos found.</p>
                            </div>
                        ) : (
                            <div className="row g-4">
                                {videos.map(vid => (
                                    <div key={vid.id} className="col-xl-3 col-lg-4 col-md-6 col-12">
                                        <VideoCard video={vid} onPlay={() => setPlayer(vid)} onDelete={() => onDelete(vid.id)} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </ContainerDashboard>
            {player && createPortal(
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
                    onClick={() => setPlayer(null)}>
                    <button onClick={() => setPlayer(null)}
                        style={{ position: 'absolute', top: 20, right: 24, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        ✕
                    </button>
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: '90vw' }}>
                        <div style={{ width: '100%', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.5)', background: '#000' }}>
                            <video
                                key={player.id}
                                src={player.videoUrl}
                                controls
                                autoPlay
                                style={{ maxWidth: '90vw', maxHeight: '80vh', display: 'block' }}
                            />
                        </div>
                        <p className="mb-0 fw-semibold" style={{ color: '#fff', fontSize: 15 }}>{player.videoName || player.name}</p>
                        <p className="mb-0" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
                            <i className="bi bi-calendar3 me-1"></i>{new Date(player.createdAt || player.uploadedAt).toLocaleDateString("en-GB")}
                        </p>
                    </div>
                </div>, document.body
            )}
        </>
    )
}

const VideoCard = ({ video, onPlay, onDelete }) => {
    const { checkAccess } = usePermissionData()
    return (
        <div className="rounded-4 overflow-hidden h-100 d-flex flex-column"
            style={{ background: '#fff', boxShadow: '0 2px 16px rgba(12,172,194,0.10)', border: '1px solid rgba(12,172,194,0.10)' }}>
            <div className="position-relative"
                style={{ height: 180, overflow: 'hidden', cursor: 'pointer', background: 'linear-gradient(135deg, #e5fafc, #fdf0ff)' }}
                onClick={onPlay}>
                {video.videoUrl ? (
                    <video
                        src={video.videoUrl}
                        preload="metadata"
                        muted
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onLoadedMetadata={e => { e.target.currentTime = 1 }}
                    />
                ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                        <i className="bi bi-film" style={{ fontSize: 48, color: '#0CACC2', opacity: 0.3 }}></i>
                    </div>
                )}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }}>
                        <i className="bi bi-play-fill" style={{ fontSize: 22, color: '#fff', marginLeft: 2 }}></i>
                    </div>
                </div>
            </div>
            <div className="d-flex flex-column p-3 flex-grow-1">
                <h6 className="fw-semibold mb-1 text-truncate" title={video.videoName || video.name} style={{ color: '#1a1a2e' }}>{video.videoName || video.name}</h6>
                <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
                    <i className="bi bi-calendar3 me-1"></i>{new Date(video.createdAt || video.uploadedAt).toLocaleDateString("en-GB")}
                </p>
                {checkAccess(4, 4) && (
                    <div className="mt-auto">
                        <button type="button" className="btn w-100 fw-semibold"
                            style={{ background: '#EC2434', color: '#fff', borderRadius: '8px', padding: '5px 14px', fontSize: '13px' }}
                            onClick={onDelete}>
                            <i className="icon-trash me-1"></i> Delete
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default VideosGallery
